import React, { useEffect, useMemo, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import {
  Loader2, Plus, Calendar, Clock, CheckCircle2, XCircle, AlertCircle,
  FileText, Trash2, Send, RotateCcw, User, Paperclip, Download, X,
  Pencil, Ban, CalendarDays,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { TeamLeaveCalendar } from "@/components/TeamLeaveCalendar";
import { useAuth } from "@/context/AuthContext";

// Cache sărbători legale pe an (pentru calculul zilelor lucrătoare din cererile de concediu)
const _holidayCache = {};
function fetchHolidayYear(y) {
  if (!_holidayCache[y]) {
    _holidayCache[y] = api.get(`/timeoff/holidays?year=${y}`)
      .then(r => (r.data || []).map(h => h.date))
      .catch(() => []);
  }
  return _holidayCache[y];
}

// Zile lucrătoare (L-V) din interval, excluzând weekend-urile ȘI sărbătorile legale
function useBusinessDays(startDate, endDate) {
  const [holidays, setHolidays] = useState(() => new Set());
  useEffect(() => {
    if (!startDate || !endDate) { setHolidays(new Set()); return; }
    const years = new Set([new Date(startDate).getFullYear(), new Date(endDate).getFullYear()]);
    let cancelled = false;
    Promise.all([...years].map(fetchHolidayYear)).then(lists => {
      if (cancelled) return;
      const all = new Set();
      for (const l of lists) for (const d of l) all.add(d);
      setHolidays(all);
    });
    return () => { cancelled = true; };
  }, [startDate, endDate]);

  return useMemo(() => {
    if (!startDate || !endDate) return 0;
    const s = new Date(startDate), e = new Date(endDate);
    if (e < s) return 0;
    let n = 0;
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (d.getDay() !== 0 && d.getDay() !== 6 && !holidays.has(iso)) n++;
    }
    return n;
  }, [startDate, endDate, holidays]);
}

const STATUS_META = {
  draft: { label: "Ciornă", cls: "bg-neutral-100 text-neutral-700 border-neutral-300", icon: FileText },
  pending_substitute: { label: "Așteaptă confirmarea înlocuitorului", cls: "bg-violet-100 text-violet-700 border-violet-300", icon: User },
  substitute_rejected: { label: "Înlocuitor respins — schimbă înlocuitorul", cls: "bg-rose-100 text-rose-700 border-rose-300", icon: XCircle },
  pending_manager: { label: "În așteptarea Managerului", cls: "bg-amber-100 text-amber-700 border-amber-300", icon: Clock },
  pending_hr: { label: "În așteptarea HR", cls: "bg-blue-100 text-blue-700 border-blue-300", icon: Clock },
  approved: { label: "Aprobată", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
  rejected: { label: "Respinsă", cls: "bg-red-100 text-red-700 border-red-300", icon: XCircle },
  withdrawn: { label: "Retrasă", cls: "bg-neutral-100 text-neutral-600 border-neutral-300", icon: RotateCcw },
  cancelled: { label: "Anulată", cls: "bg-neutral-100 text-neutral-600 border-neutral-300", icon: X },
  pending_cancellation: { label: "Anulare — așteaptă Manager", cls: "bg-orange-100 text-orange-700 border-orange-300", icon: Clock },
};

const CLIENT_SITUATION_OPTIONS = [
  { value: "informed_agreed", label: "Clientul a fost informat și și-a dat acordul." },
  { value: "needs_physical_substitute", label: "Este necesar un înlocuitor suplimentar cu prezență fizică." },
  { value: "not_applicable", label: "Nu este aplicabil / activitatea nu presupune prezență la client." },
];

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, cls: "bg-neutral-100" };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${meta.cls}`}>
      {Icon && <Icon className="h-3 w-3" />} {meta.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}

export default function TimeOff() {
  const [balances, setBalances] = useState([]);
  const [usage, setUsage] = useState([]);
  const [requests, setRequests] = useState([]);
  const [types, setTypes] = useState([]);
  const [team, setTeam] = useState([]);
  const [manager, setManager] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [projects, setProjects] = useState([]);

  async function load() {
    setLoading(true);
    try {
      const [b, r, t, mgr, tm, prj, usg] = await Promise.all([
        api.get("/timeoff/balances/me"),
        api.get("/timeoff/requests/mine"),
        api.get("/timeoff/types"),
        api.get("/timeoff/org/my-manager"),
        api.get("/timeoff/org/colleagues"),
        api.get("/projects"),
        api.get("/timeoff/usage/me"),
      ]);
      setBalances(b.data.balances);
      setRequests(r.data);
      setTypes(t.data.filter(x => x.is_active));
      setManager(mgr.data.manager);
      setTeam(tm.data || []);
      setProjects(prj.data || []);
      setUsage(usg.data.items || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px]">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="section-eyebrow">Concedii & absențe</p>
            <h1 className="font-display font-semibold text-[42px] tracking-[-0.02em] text-ink mb-2 leading-tight">Concedii & absențe</h1>
            <p className="text-ink-muted text-[15px] max-w-xl">
              Gestionează cererile de concediu, verifică soldul disponibil și urmărește starea aprobărilor tale.
            </p>
          </div>
          <button
            onClick={() => setOpenForm(true)}
            data-testid="btn-new-timeoff"
            className="inline-flex items-center gap-2 bg-ink text-white px-6 py-3.5 rounded-xl font-semibold text-[14px] hover:bg-brand transition-colors shadow-sm"
          >
            <Plus className="h-4 w-4" /> Cerere nouă
          </button>
        </div>

        {/* Balances */}
        <div className="mb-10">
          <div className="mb-4">
            <div className="section-eyebrow">Soldul meu de zile ({new Date().getFullYear()})</div>
            <div className="section-title">Sumar concedii</div>
          </div>
          {(() => {
            const annual = balances.find(b => b.type?.code === "annual" || /odihn/i.test(b.type?.name || ""));
            const pendingRequests = requests.filter(r => ["pending_manager", "pending_hr", "pending_cancellation"].includes(r.status));
            const now = new Date();
            const upcoming = requests
              .filter(r => r.status === "approved" && new Date(r.start_date) >= new Date(now.getFullYear(), now.getMonth(), now.getDate()))
              .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))[0];
            const ytdUsedDays = requests
              .filter(r => r.status === "approved" && new Date(r.start_date).getFullYear() === now.getFullYear())
              .reduce((s, r) => s + (r.business_days || 0), 0);

            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Card 1 — Concediu de odihnă */}
                {annual ? (
                  <div className="surface surface-interactive p-6">
                    <div className="flex items-center gap-2.5 mb-4">
                      <div className="h-2.5 w-2.5 rounded-full" style={{ background: annual.type.color }} />
                      <span className="text-[13px] font-display font-medium text-ink-muted">{annual.type.name}</span>
                    </div>
                    <div className="text-[42px] font-display font-semibold text-ink leading-none tabular-nums">{annual.remaining}</div>
                    <div className="text-[13px] text-ink-muted mt-2">zile rămase din {annual.total}</div>
                    <div className="mt-4 pt-4 border-t border-neutral-100 flex gap-4 text-[13px]">
                      <span className="text-emerald-700 font-medium">✓ {annual.used} folosite</span>
                      {annual.pending > 0 && <span className="text-amber-700 font-medium">⏱ {annual.pending} în așteptare</span>}
                    </div>
                  </div>
                ) : (
                  <div className="surface p-6 flex items-center justify-center text-[13px] text-ink-muted">
                    Fără sold configurat
                  </div>
                )}

                {/* Card 2 — Cereri în așteptare */}
                <div className="surface surface-interactive p-6">
                  <div className="flex items-center gap-2.5 mb-4">
                    <Clock className="h-4 w-4 text-amber-600" />
                    <span className="text-[13px] font-display font-medium text-ink-muted">Cereri în așteptare</span>
                  </div>
                  <div className="text-[42px] font-display font-semibold text-ink leading-none tabular-nums">
                    {pendingRequests.length}
                  </div>
                  <div className="text-[13px] text-ink-muted mt-2">
                    {pendingRequests.length === 0 ? "nicio cerere activă"
                      : pendingRequests.length === 1 ? "cerere de procesat"
                      : "cereri de procesat"}
                  </div>
                  <div className="mt-4 pt-4 border-t border-neutral-100 text-[13px] text-ink-muted min-h-[20px]">
                    {pendingRequests[0]
                      ? <>Următoarea: <b className="text-ink">{fmtDate(pendingRequests[0].start_date)}</b></>
                      : "Fără cereri în proces"}
                  </div>
                </div>

                {/* Card 3 — Concediu viitor */}
                <div className="surface surface-interactive p-6">
                  <div className="flex items-center gap-2.5 mb-4">
                    <Calendar className="h-4 w-4 text-brand" />
                    <span className="text-[13px] font-display font-medium text-ink-muted">Concediu viitor</span>
                  </div>
                  {upcoming ? (
                    <>
                      <div className="text-[28px] font-display font-semibold text-ink leading-tight tabular-nums">
                        {fmtDate(upcoming.start_date)}
                      </div>
                      <div className="text-[13px] text-ink-muted mt-2">
                        <b className="text-ink">{upcoming.business_days}</b> {upcoming.business_days === 1 ? "zi" : "zile"} · {upcoming.type?.name}
                      </div>
                      <div className="mt-4 pt-4 border-t border-neutral-100 text-[13px] text-ink-muted">
                        până la {fmtDate(upcoming.end_date)}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-[28px] font-display font-semibold text-ink-muted leading-tight">—</div>
                      <div className="text-[13px] text-ink-muted mt-2">Nu ai concediu planificat</div>
                      <div className="mt-4 pt-4 border-t border-neutral-100 text-[13px] text-ink-muted">
                        Poți solicita unul din butonul de sus
                      </div>
                    </>
                  )}
                </div>

                {/* Card 4 — Zile folosite anul acesta */}
                <div className="surface surface-interactive p-6">
                  <div className="flex items-center gap-2.5 mb-4">
                    <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                    <span className="text-[13px] font-display font-medium text-ink-muted">Zile folosite {now.getFullYear()}</span>
                  </div>
                  <div className="text-[42px] font-display font-semibold text-ink leading-none tabular-nums">
                    {ytdUsedDays}
                  </div>
                  <div className="text-[13px] text-ink-muted mt-2">
                    zile aprobate anul acesta
                  </div>
                  <div className="mt-4 pt-4 border-t border-neutral-100 text-[13px] text-ink-muted">
                    Din toate tipurile de concediu
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Sumar pe fiecare tip de concediu — an curent */}
        {(() => {
          const active = usage.filter(u => (u.used || 0) > 0 || (u.pending || 0) > 0);
          return (
            <div className="mb-10" data-testid="timeoff-usage-breakdown">
              <div className="mb-4">
                <div className="section-eyebrow">Detaliat pe tip ({new Date().getFullYear()})</div>
                <div className="section-title">Sumar pe fiecare tip de concediu</div>
              </div>
              {active.length === 0 ? (
                <div className="surface p-6 text-[13px] text-ink-muted">
                  Nu ai zile înregistrate anul acesta pe niciun tip de concediu.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {active.map(u => (
                    <div key={u.type.id} className="surface p-4" data-testid={`usage-type-${u.type.code}`}>
                      <div className="flex items-center gap-2.5 mb-2">
                        <div className="h-2.5 w-2.5 rounded-full" style={{ background: u.type.color }} />
                        <span className="text-[13px] font-display font-medium text-ink">{u.type.name}</span>
                      </div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-[28px] font-display font-semibold text-ink leading-none tabular-nums">{u.used}</span>
                        <span className="text-[12px] text-ink-muted">
                          {u.type.unit === "hours" ? "ore folosite" : "zile folosite"}
                        </span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-neutral-100 flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
                        {u.pending > 0 && <span className="text-amber-700 font-medium">⏱ {u.pending} în așteptare</span>}
                        {u.deducts_from_balance
                          ? <span className="text-ink-muted">{u.remaining} rămase din {u.total}</span>
                          : <span className="text-ink-muted">fără scădere din sold</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Approval chain info */}
        <div className="mb-8 surface p-5 flex items-start gap-4 bg-brand/[0.03] border-brand/20">
          <div className="h-10 w-10 rounded-xl bg-brand/10 text-brand flex items-center justify-center shrink-0">
            <User className="h-5 w-5" />
          </div>
          <div className="text-[14px]">
            <div className="font-semibold text-ink">Fluxul de aprobare</div>
            <div className="text-ink-muted mt-1">
              Cererile tale sunt aprobate de <b className="text-ink">{manager ? `${manager.name} (${manager.job_title})` : "Managerul Superior — nu e configurat, contactează HR"}</b>, apoi de HR.
            </div>
          </div>
        </div>

        {/* My requests */}
        <div>
          <div className="mb-4">
            <div className="section-eyebrow">Istoric</div>
            <div className="section-title">Cererile mele</div>
          </div>
          {loading ? (
            <div className="surface flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
          ) : requests.length === 0 ? (
            <div className="surface text-center py-16 text-ink-muted">
              <Calendar className="h-8 w-8 mx-auto text-ink-muted mb-3" />
              <p className="text-sm">Nu ai cereri încă. Apasă „Cerere nouă&quot; pentru a solicita concediu.</p>
            </div>
          ) : (
            <>
            {/* Desktop — tabel */}
            <div className="surface overflow-x-auto hidden md:block">
              <table className="w-full text-[14px]">
                <thead className="border-b border-neutral-200 bg-neutral-50/50">
                  <tr className="text-left">
                    <th className="p-4 font-display text-[12px] font-semibold text-ink-muted">Tip</th>
                    <th className="p-4 font-display text-[12px] font-semibold text-ink-muted">Perioadă</th>
                    <th className="p-4 font-display text-[12px] font-semibold text-ink-muted">Zile</th>
                    <th className="p-4 font-display text-[12px] font-semibold text-ink-muted">Status</th>
                    <th className="p-4 font-display text-[12px] font-semibold text-ink-muted">Trimisă</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(r => (
                    <tr key={r.id} className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50/50 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-2.5">
                          <div className="h-2.5 w-2.5 rounded-full" style={{ background: r.type?.color }} />
                          <span className="font-medium text-ink">{r.type?.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-ink-muted">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</td>
                      <td className="p-4 font-mono text-ink font-semibold">{r.business_days}</td>
                      <td className="p-4"><StatusBadge status={r.status} /></td>
                      <td className="p-4 text-ink-muted text-[12px]">
                        {r.submitted_at ? fmtDate(r.submitted_at) : "-"}
                      </td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => setDetail(r)}
                          className="text-brand text-[13px] font-semibold hover:underline"
                        >
                          Detalii
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobil — carduri */}
            <div className="md:hidden space-y-3" data-testid="timeoff-requests-cards">
              {requests.map(r => (
                <button key={r.id} onClick={() => setDetail(r)}
                  data-testid={`timeoff-card-${r.id}`}
                  className="surface surface-interactive w-full text-left p-4 active:scale-[0.99]">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: r.type?.color }} />
                      <span className="font-semibold text-ink truncate">{r.type?.name}</span>
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="mt-3 flex items-center justify-between text-[13px]">
                    <span className="text-ink-muted">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span>
                    <span className="font-mono font-semibold text-ink">{r.business_days} zile</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[12px] text-ink-muted">
                    <span>Trimisă: {r.submitted_at ? fmtDate(r.submitted_at) : "-"}</span>
                    <span className="text-brand font-semibold">Detalii →</span>
                  </div>
                </button>
              ))}
            </div>
            </>
          )}
        </div>

        {/* Calendar echipă — cine e în concediu */}
        <div className="mt-12">
          <div className="mb-4 flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-brand" />
            <div>
              <div className="section-eyebrow">Echipă</div>
              <div className="section-title">Cine e în concediu</div>
            </div>
          </div>
          <p className="text-ink-muted text-sm mb-5 max-w-xl">
            Vezi ce colegi sunt în concediu pe fiecare zi a lunii (doar cererile aprobate) — util când îți planifici propriul concediu.
          </p>
          <TeamLeaveCalendar />
        </div>
      </div>

      <RequestForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        types={types}
        team={team}
        projects={projects}
        onSaved={async () => { setOpenForm(false); await load(); }}
      />

      <RequestDetail
        request={detail}
        onClose={() => setDetail(null)}
        onEdit={(r) => { setDetail(null); setEditing(r); }}
        onRevoke={(r) => { setDetail(null); setRevoking(r); }}
        onChanged={async () => { setDetail(null); await load(); }}
      />

      <EditRequestForm
        request={editing}
        types={types}
        team={team}
        projects={projects}
        onClose={() => setEditing(null)}
        onSaved={async () => { setEditing(null); await load(); }}
      />

      <RevokeDialog
        request={revoking}
        onClose={() => setRevoking(null)}
        onSaved={async () => { setRevoking(null); await load(); }}
      />
    </PortalLayout>
  );
}


function RequestForm({ open, onClose, types, team, projects, onSaved }) {
  const [typeId, setTypeId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [substituteId, setSubstituteId] = useState("");
  const [affectedProjects, setAffectedProjects] = useState([]);
  const [clientSituation, setClientSituation] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]); // File[]

  useEffect(() => {
    if (open) {
      setTypeId(""); setStartDate(""); setEndDate("");
      setReason(""); setSubstituteId(""); setAffectedProjects([]);
      setClientSituation(""); setAttachments([]);
    }
  }, [open]);

  const selectedType = types.find(t => t.id === typeId);
  const requiresDoc = selectedType?.requires_document;

  const dayCount = useBusinessDays(startDate, endDate);

  async function save(submit) {
    if (!typeId || !startDate || !endDate) {
      toast.error("Completează tipul și perioada");
      return;
    }
    if (submit && !clientSituation) {
      toast.error("Alege o opțiune la „Situație client”");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/timeoff/requests", {
        type_id: typeId,
        start_date: startDate,
        end_date: endDate,
        reason,
        substitute_id: substituteId || null,
        affected_projects: affectedProjects,
        client_situation: clientSituation || null,
        submit_now: submit,
      });
      // Upload attachments
      for (const f of attachments) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post(`/timeoff/requests/${data.id}/attachment`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
        } catch (e) { toast.error(`Upload eșuat: ${f.name}`); }
      }
      toast.success(submit ? "Cerere trimisă spre aprobare" : "Ciornă salvată");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Cerere concediu / absență</DialogTitle>
          <DialogDescription>Completează detaliile cererii. Poți salva ca ciornă și trimite mai târziu.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">Tip cerere *</label>
            <select value={typeId} onChange={e => setTypeId(e.target.value)}
              className="w-full mt-1 border border-neutral-300 px-3 py-2 text-sm">
              <option value="">— alege —</option>
              {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">Data început *</label>
              <DateInput value={startDate} onChange={e => setStartDate(e.target.value)}
                className="w-full mt-1 border border-neutral-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">Data final *</label>
              <DateInput value={endDate} onChange={e => setEndDate(e.target.value)}
                className="w-full mt-1 border border-neutral-300 px-3 py-2 text-sm" />
            </div>
          </div>

          {dayCount > 0 && (
            <div className="text-xs bg-brand/5 border-l-2 border-brand p-2 text-ink">
              <b>{dayCount}</b> zile lucrătoare (fără weekend și sărbători legale) — acestea se scad din concediu
            </div>
          )}

          <div>
            <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">Motiv / observații</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
              className="w-full mt-1 border border-neutral-300 px-3 py-2 text-sm resize-none" />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">Înlocuitor (opțional)</label>
            <select value={substituteId} onChange={e => setSubstituteId(e.target.value)}
              className="w-full mt-1 border border-neutral-300 px-3 py-2 text-sm">
              <option value="">— fără —</option>
              {team.map(t => <option key={t.id} value={t.id}>{t.name} ({t.job_title})</option>)}
            </select>
            <p className="text-[11px] text-ink-muted mt-1">Selectează un coleg care preia responsabilitățile. Va trebui să confirme înlocuirea înainte de aprobare.</p>
          </div>

          <div data-testid="client-situation-section">
            <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">
              Situație client <span className="text-red-600">*</span>
            </label>
            <div className="mt-2 space-y-2">
              {CLIENT_SITUATION_OPTIONS.map(opt => (
                <label key={opt.value}
                  data-testid={`client-situation-${opt.value}`}
                  className={`flex items-start gap-2.5 border px-3 py-2.5 text-sm cursor-pointer transition-colors ${
                    clientSituation === opt.value ? "border-brand bg-brand/5" : "border-neutral-300 hover:border-neutral-400"
                  }`}>
                  <input type="radio" name="client_situation" className="mt-0.5"
                    checked={clientSituation === opt.value}
                    onChange={() => setClientSituation(opt.value)} />
                  <span className="text-ink">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">Proiecte afectate</label>
            <div className="mt-1 border border-neutral-300 p-2 max-h-32 overflow-y-auto text-sm">
              {projects.length === 0 && <div className="text-ink-muted text-xs">Fără proiecte disponibile</div>}
              {projects.map(p => (
                <label key={p.id} className="flex items-center gap-2 py-1">
                  <input type="checkbox" checked={affectedProjects.includes(p.id)}
                    onChange={e => setAffectedProjects(prev =>
                      e.target.checked ? [...prev, p.id] : prev.filter(x => x !== p.id))} />
                  <span>{p.code} — {p.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">
              Documente justificative {requiresDoc && <span className="text-red-600">*</span>}
            </label>
            <div className="mt-1 border-2 border-dashed border-neutral-300 p-4 text-center">
              <input id="timeoff-files" type="file" multiple className="hidden"
                onChange={e => setAttachments([...attachments, ...Array.from(e.target.files || [])])} />
              <label htmlFor="timeoff-files" className="cursor-pointer text-sm text-brand hover:underline">
                <Paperclip className="h-4 w-4 inline mr-1" /> Adaugă fișier(e)
              </label>
              {attachments.length > 0 && (
                <div className="mt-2 space-y-1 text-left">
                  {attachments.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-neutral-50 p-1">
                      <span>{f.name}</span>
                      <button onClick={() => setAttachments(attachments.filter((_, j) => j !== i))}>
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-neutral-200">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink">Anulează</button>
          <button onClick={() => save(false)} disabled={busy}
            className="px-4 py-2 text-sm border border-neutral-300 hover:border-brand">
            Salvează ciornă
          </button>
          <button onClick={() => save(true)} disabled={busy}
            data-testid="btn-submit-timeoff"
            className="px-5 py-2 text-sm bg-brand text-white hover:opacity-90 inline-flex items-center gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Trimite spre aprobare
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function RequestDetail({ request, onClose, onChanged, onEdit, onRevoke }) {
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  if (!request) return null;

  async function action(url, successMsg) {
    setBusy(true);
    try {
      await api.post(url);
      toast.success(successMsg);
      onChanged();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  async function deleteRequest() {
    if (!window.confirm("Ștergi definitiv această cerere? Zilele de concediu revin automat la cele disponibile.")) return;
    setBusy(true);
    try {
      await api.delete(`/timeoff/requests/${request.id}`);
      toast.success("Cerere ștearsă — zilele au revenit la cele disponibile");
      onChanged();
      onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const isFuture = new Date(request.start_date) > new Date();
  const canEditApproved = request.status === "approved" && isFuture;
  const canRevokeApproved = request.status === "approved" && isFuture;

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-3">
            {request.type?.name}
            <StatusBadge status={request.status} />
          </DialogTitle>
          <DialogDescription>
            {fmtDate(request.start_date)} → {fmtDate(request.end_date)} ({request.business_days} zile lucrătoare)
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mt-3">
          {request.reason && (
            <div className="col-span-2">
              <div className="text-xs uppercase font-mono text-ink-muted mb-1">Motiv</div>
              <div className="text-ink">{request.reason}</div>
            </div>
          )}
          {request.substitute && (
            <div>
              <div className="text-xs uppercase font-mono text-ink-muted mb-1">Înlocuitor</div>
              <div>{request.substitute.name} — {request.substitute.job_title}</div>
              {request.status === "pending_substitute" && (
                <div className="text-[11px] text-violet-700 mt-0.5">Așteaptă confirmarea înlocuitorului</div>
              )}
              {request.substitute_confirmed_at && (
                <div className="text-[11px] text-emerald-700 mt-0.5">✓ Înlocuire confirmată</div>
              )}
              {request.status === "substitute_rejected" && (
                <div className="text-[11px] text-rose-700 mt-0.5">✗ Înlocuire respinsă{request.substitute_comment ? ` — ${request.substitute_comment}` : ""}</div>
              )}
            </div>
          )}
          {request.client_situation_label && (
            <div className="col-span-2">
              <div className="text-xs uppercase font-mono text-ink-muted mb-1">Situație client</div>
              <div className="text-ink">{request.client_situation_label}</div>
            </div>
          )}
          {request.affected_projects?.length > 0 && (
            <div>
              <div className="text-xs uppercase font-mono text-ink-muted mb-1">Proiecte afectate</div>
              <div className="text-ink-muted">{request.affected_projects.length} proiect(e)</div>
            </div>
          )}
        </div>

        {request.documents?.length > 0 && (
          <div className="mt-4">
            <div className="text-xs uppercase font-mono text-ink-muted mb-2">Documente atașate</div>
            <div className="space-y-1">
              {request.documents.map(d => (
                <a key={d.id}
                  href={`${backendUrl}/api/timeoff/attachments/${request.id}/${d.id}/download`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center justify-between text-xs bg-neutral-50 p-2 hover:bg-neutral-100">
                  <span className="flex items-center gap-2"><FileText className="h-3.5 w-3.5" />{d.filename}</span>
                  <Download className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4">
          <div className="text-xs uppercase font-mono text-ink-muted mb-2">Istoric aprobări</div>
          <div className="space-y-2 text-sm">
            {(request.history || []).map((h, i) => (
              <div key={i} className="flex gap-3 border-l-2 border-neutral-200 pl-3 py-1">
                <div className="text-ink-muted text-xs min-w-[110px]">
                  {`${fmtDate(h.at)}, ${new Date(h.at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}`}
                </div>
                <div>
                  <div className="font-medium text-ink">{h.by_name || "-"} — {actionLabel(h.action)}</div>
                  {h.comment && <div className="text-ink-muted text-xs mt-0.5">{h.comment}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-neutral-200 flex-wrap">
          {request.status === "draft" && (
            <>
              <button disabled={busy}
                onClick={() => action(`/timeoff/requests/${request.id}/submit`, "Cerere trimisă")}
                className="px-4 py-2 text-sm bg-brand text-white hover:opacity-90">
                Trimite spre aprobare
              </button>
            </>
          )}
          {request.status === "substitute_rejected" && (
            <button
              data-testid="btn-edit-substitute-rejected"
              onClick={() => onEdit(request)}
              className="px-4 py-2 text-sm bg-brand text-white hover:opacity-90 inline-flex items-center gap-2"
            >
              <Pencil className="h-4 w-4" /> Schimbă înlocuitorul și retrimite
            </button>
          )}
          {["pending_manager", "pending_hr", "pending_substitute"].includes(request.status) && (
            <button disabled={busy}
              onClick={() => action(`/timeoff/requests/${request.id}/withdraw`, "Cerere retrasă")}
              className="px-4 py-2 text-sm border border-neutral-300 hover:border-brand">
              Retrage cererea
            </button>
          )}
          {canEditApproved && (
            <button
              data-testid="btn-edit-approved"
              onClick={() => onEdit(request)}
              className="px-4 py-2 text-sm border border-neutral-300 hover:border-brand inline-flex items-center gap-2"
            >
              <Pencil className="h-4 w-4" /> Editează
            </button>
          )}
          {canRevokeApproved && (
            <button
              data-testid="btn-revoke-approved"
              onClick={() => onRevoke(request)}
              className="px-4 py-2 text-sm bg-red-600 text-white hover:opacity-90 inline-flex items-center gap-2"
            >
              <Ban className="h-4 w-4" /> Revocă
            </button>
          )}
          {request.status === "pending_cancellation" && (
            <div className="text-xs text-orange-700 bg-orange-50 border border-orange-200 px-3 py-2">
              Cerere de anulare trimisă — așteaptă confirmarea Managerului Superior
            </div>
          )}
          {isAdmin && (
            <button onClick={deleteRequest} disabled={busy}
              data-testid="btn-delete-request"
              className="px-4 py-2 text-sm border border-red-300 text-red-600 hover:bg-red-50 inline-flex items-center gap-2">
              <Trash2 className="h-4 w-4" /> Șterge definitiv
            </button>
          )}
          <button onClick={onClose} className="px-4 py-2 text-sm text-ink-muted hover:text-ink">Închide</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function actionLabel(a) {
  return {
    create: "cerere creată",
    submit: "trimisă spre aprobare",
    substitute_confirm: "înlocuire confirmată",
    substitute_reject: "înlocuire respinsă",
    manager_approve: "aprobată de manager",
    manager_reject: "respinsă de manager",
    hr_approve: "aprobată de HR",
    hr_reject: "respinsă de HR",
    withdraw: "retrasă",
    cancel: "anulată",
    modify: "modificată de angajat",
    request_cancel: "solicitare anulare de la angajat",
    approve_cancel: "anulare aprobată de manager",
    reject_cancel: "anulare respinsă — cerere rămâne aprobată",
  }[a] || a;
}


function EditRequestForm({ request, types, team, projects, onClose, onSaved }) {
  const [typeId, setTypeId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [substituteId, setSubstituteId] = useState("");
  const [affectedProjects, setAffectedProjects] = useState([]);
  const [clientSituation, setClientSituation] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (request) {
      setTypeId(request.type_id || "");
      setStartDate(request.start_date || "");
      setEndDate(request.end_date || "");
      setReason(request.reason || "");
      setSubstituteId(request.substitute_id || "");
      setAffectedProjects(request.affected_projects || []);
      setClientSituation(request.client_situation || "");
    }
  }, [request]);

  const dayCount = useBusinessDays(startDate, endDate);

  if (!request) return null;

  async function save() {
    if (!typeId || !startDate || !endDate) {
      toast.error("Completează tipul și perioada");
      return;
    }
    if (!clientSituation) {
      toast.error("Alege o opțiune la „Situație client”");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/timeoff/requests/${request.id}`, {
        type_id: typeId,
        start_date: startDate,
        end_date: endDate,
        reason,
        substitute_id: substituteId || null,
        affected_projects: affectedProjects,
        client_situation: clientSituation,
      });
      toast.success("Cerere modificată — retrimisă spre aprobare");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Modifică cererea de concediu</DialogTitle>
          <DialogDescription>
            După salvare, cererea revine automat la <b>În așteptarea Managerului</b> și va urma din nou fluxul de aprobare.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">Tip cerere *</label>
            <select value={typeId} onChange={e => setTypeId(e.target.value)}
              className="w-full mt-1 border border-neutral-300 px-3 py-2 text-sm">
              {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">Data început *</label>
              <DateInput value={startDate} onChange={e => setStartDate(e.target.value)}
                data-testid="edit-timeoff-start"
                className="w-full mt-1 border border-neutral-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">Data final *</label>
              <DateInput value={endDate} onChange={e => setEndDate(e.target.value)}
                data-testid="edit-timeoff-end"
                className="w-full mt-1 border border-neutral-300 px-3 py-2 text-sm" />
            </div>
          </div>
          {dayCount > 0 && (
            <div className="text-xs bg-brand/5 border-l-2 border-brand p-2 text-ink">
              <b>{dayCount}</b> zile lucrătoare (fără weekend și sărbători legale) — acestea se scad din concediu
            </div>
          )}
          <div>
            <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">Motiv / observații</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
              className="w-full mt-1 border border-neutral-300 px-3 py-2 text-sm resize-none" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">Înlocuitor (opțional)</label>
            <select value={substituteId} onChange={e => setSubstituteId(e.target.value)}
              data-testid="edit-timeoff-substitute"
              className="w-full mt-1 border border-neutral-300 px-3 py-2 text-sm">
              <option value="">— fără —</option>
              {team.map(t => <option key={t.id} value={t.id}>{t.name} ({t.job_title})</option>)}
            </select>
            <p className="text-[11px] text-ink-muted mt-1">Dacă schimbi înlocuitorul, acesta va trebui să confirme din nou.</p>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">
              Situație client <span className="text-red-600">*</span>
            </label>
            <div className="mt-2 space-y-2">
              {CLIENT_SITUATION_OPTIONS.map(opt => (
                <label key={opt.value}
                  className={`flex items-start gap-2.5 border px-3 py-2.5 text-sm cursor-pointer transition-colors ${
                    clientSituation === opt.value ? "border-brand bg-brand/5" : "border-neutral-300 hover:border-neutral-400"
                  }`}>
                  <input type="radio" name="edit_client_situation" className="mt-0.5"
                    checked={clientSituation === opt.value}
                    onChange={() => setClientSituation(opt.value)} />
                  <span className="text-ink">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-neutral-200">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink">Anulează</button>
          <button onClick={save} disabled={busy}
            data-testid="btn-save-edit-timeoff"
            className="px-5 py-2 text-sm bg-brand text-white hover:opacity-90 inline-flex items-center gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Salvează și trimite spre re-aprobare
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function RevokeDialog({ request, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (request) setReason(""); }, [request]);

  if (!request) return null;

  async function submit() {
    setBusy(true);
    try {
      await api.post(`/timeoff/requests/${request.id}/request-cancel`, {
        comment: reason || "Solicitare anulare",
      });
      toast.success("Solicitare de anulare trimisă către Manager");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Revocă cererea de concediu</DialogTitle>
          <DialogDescription>
            Cererea a fost deja <b>aprobată</b>. Pentru a o revoca este necesară confirmarea Managerului Superior.
            Explică motivul revocării mai jos.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <div className="text-xs bg-neutral-50 border border-neutral-200 p-3 mb-3">
            <div className="font-medium text-ink">{request.type?.name}</div>
            <div className="text-ink-muted">{fmtDate(request.start_date)} → {fmtDate(request.end_date)}</div>
          </div>
          <label className="text-xs font-mono uppercase tracking-wide text-ink-muted">Motiv revocare</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={4}
            data-testid="revoke-reason-input"
            placeholder="Ex: Am nevoie să reprogramez concediul pentru altă perioadă..."
            className="w-full mt-1 border border-neutral-300 px-3 py-2 text-sm resize-none" />
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-neutral-200">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink">Anulează</button>
          <button onClick={submit} disabled={busy}
            data-testid="btn-confirm-revoke"
            className="px-5 py-2 text-sm bg-red-600 text-white hover:opacity-90 inline-flex items-center gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
            Trimite solicitarea de revocare
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
